import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput as RNTextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Card, Divider } from 'react-native-paper';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from '../redux/store';
import { useGetFORoomssQuery } from '../redux/services/foroomsApi';
import {
  useGetPublicPlacesQuery,
  useGetPublicPlacesSummaryQuery,
  HKPublicPlace,
} from '../redux/services/hkpublicplaceApi';
import { useGetEmployeesQuery } from '../redux/services/emmemployeeApi';
import { useInsertPublicCleaningMutation } from '../redux/services/hkpubliccleaningApi';
import { useGetHKMProductsQuery } from '../redux/services/hkmproductApi';
import { useGetHKStocksQuery } from '../redux/services/hkreportsApi';
import { useGetSTMProductsQuery } from '../redux/services/stmproductApi';
import { useGetSTStocksQuery } from '../redux/services/streportsApi';
import { ShowToast, getContrastColor } from '../Common/common';
import {
  renderRoomBadges,
  getRoomStatusLabel,
  getRoomColor,
} from '../utils/statusScreenUtils';
import Checkbox from '../Components/Checkbox';
import { Picker } from '@react-native-picker/picker';

import T from '../Components/T';
import { tp } from '../utils/translateText';
type ActiveTab = 'ROOMS' | 'PUBLIC_AREAS' | 'PRODUCTS_STOCK';
type ProductSource = 'HK' | 'STORE';

const getPlaceCategoryIcon = (type: string): string => {
  switch (type) {
    case 'Reception':
      return 'users';
    case 'Restaurant':
      return 'cutlery';
    case 'Bar':
      return 'glass';
    case 'Lobby':
      return 'home';
    case 'Restroom':
      return 'bath';
    case 'Banquet':
      return 'calendar';
    case 'Gym':
      return 'heartbeat';
    case 'Pool':
      return 'tint';
    case 'Corridor':
      return 'arrows-h';
    default:
      return 'building';
  }
};

const getPlaceStatusDetails = (status?: string, isOverdue?: number) => {
  if (status === 'I') {
    return {
      label: 'In Progress',
      color: '#0288d1',
      bgColor: '#e1f5fe',
    };
  }
  if (status === 'M') {
    return {
      label: 'Maintenance',
      color: '#7b1fa2',
      bgColor: '#f3e5f5',
    };
  }
  if (status === 'D' || isOverdue) {
    return {
      label: isOverdue ? 'Overdue' : 'Needs Cleaning',
      color: '#d32f2f',
      bgColor: '#ffebee',
    };
  }
  return {
    label: 'Clean',
    color: '#2e7d32',
    bgColor: '#e8f5e9',
  };
};

const HKStatusScreen = ({ navigation }: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;
  const todayStr = moment(gdata.today || new Date()).format('YYYY-MM-DD');

  // Navigation tab
  const [activeTab, setActiveTab] = useState<ActiveTab>('ROOMS');

  // Room status filters
  const [roomStatusFilter, setRoomStatusFilter] = useState<string>('ALL');
  const [roomSearch, setRoomSearch] = useState<string>('');

  // Public areas state
  const [placeCategoryFilter, setPlaceCategoryFilter] = useState<string>('ALL');
  const [placeSearch, setPlaceSearch] = useState<string>('');
  const [cleanDialog, setCleanDialog] = useState<{ open: boolean; place: HKPublicPlace | null }>({
    open: false,
    place: null,
  });
  const [selectedEmployee, setSelectedEmployee] = useState<number>(0);
  const [targetPlaceStatus, setTargetPlaceStatus] = useState<string>('C');
  const [selectedPlaceTasks, setSelectedPlaceTasks] = useState<string[]>([]);
  const [isSavingClean, setIsSavingClean] = useState(false);

  // Products & Stock state
  const [productSource, setProductSource] = useState<ProductSource>('HK');
  const [productSearch, setProductSearch] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('ALL');

  // Queries
  const {
    data: roomsData = [],
    isLoading: isRoomsLoading,
    refetch: refetchRooms,
  } = useGetFORoomssQuery(cid, { skip: !cid });

  const {
    data: placesData = [],
    isLoading: isPlacesLoading,
    refetch: refetchPlaces,
  } = useGetPublicPlacesQuery(cid, { skip: !cid });

  const {
    data: summaryData,
    refetch: refetchSummary,
  } = useGetPublicPlacesSummaryQuery(cid, { skip: !cid });

  const { data: employeeData = [] } = useGetEmployeesQuery(cid, { skip: !cid });
  const [insertPublicCleaning] = useInsertPublicCleaningMutation();

  // HK Products & Stock
  const {
    data: hkProductsData = [],
    isLoading: isHKProductsLoading,
    refetch: refetchHKProducts,
  } = useGetHKMProductsQuery(cid, { skip: !cid });

  const {
    data: hkStocksData = [],
    isLoading: isHKStocksLoading,
    refetch: refetchHKStocks,
  } = useGetHKStocksQuery(
    { cid, fdt: todayStr, tdt: todayStr },
    { skip: !cid }
  );

  // Store Products & Stock
  const {
    data: storeProductsData = [],
    isLoading: isStoreProductsLoading,
    refetch: refetchStoreProducts,
  } = useGetSTMProductsQuery(cid, { skip: !cid });

  const {
    data: storeStocksData = [],
    isLoading: isStoreStocksLoading,
    refetch: refetchStoreStocks,
  } = useGetSTStocksQuery(
    { cid, fdt: todayStr, tdt: todayStr },
    { skip: !cid }
  );

  const [refreshing, setRefreshing] = useState(false);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'ROOMS') {
        await refetchRooms();
      } else if (activeTab === 'PUBLIC_AREAS') {
        await Promise.all([refetchPlaces(), refetchSummary()]);
      } else {
        await Promise.all([
          refetchHKProducts(),
          refetchHKStocks(),
          refetchStoreProducts(),
          refetchStoreStocks(),
        ]);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // --- Rooms computed data ---
  const roomCounts = useMemo(() => {
    const list = Array.isArray(roomsData) ? roomsData : [];
    return {
      all: list.length,
      dirty: list.filter((r) => r.rm_status === 'D').length,
      vacant: list.filter((r) => r.rm_status === 'V').length,
      occupied: list.filter((r) => r.rm_status === 'O').length,
      block: list.filter((r) => r.rm_status === 'B').length,
      maintenance: list.filter((r) => ['M', 'R'].includes(r.rm_status)).length,
    };
  }, [roomsData]);

  const filteredRooms = useMemo(() => {
    let list = Array.isArray(roomsData) ? roomsData : [];
    if (roomStatusFilter !== 'ALL') {
      if (roomStatusFilter === 'M') {
        list = list.filter((r) => ['M', 'R'].includes(r.rm_status));
      } else {
        list = list.filter((r) => r.rm_status === roomStatusFilter);
      }
    }
    if (roomSearch.trim()) {
      const q = roomSearch.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.rm_name?.toLowerCase().includes(q) ||
          r.rt_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [roomsData, roomStatusFilter, roomSearch]);

  // --- Public Places computed data ---
  const publicCategories = [
    'ALL',
    'Reception',
    'Restaurant',
    'Bar',
    'Lobby',
    'Restroom',
    'Banquet',
    'Gym',
    'Pool',
    'Corridor',
    'Other',
  ];

  const filteredPlaces: HKPublicPlace[] = useMemo(() => {
    let list = Array.isArray(placesData) ? placesData : [];
    if (placeCategoryFilter !== 'ALL') {
      list = list.filter((p) => p.pl_type === placeCategoryFilter);
    }
    if (placeSearch.trim()) {
      const q = placeSearch.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.pl_name?.toLowerCase().includes(q) ||
          p.pl_code?.toLowerCase().includes(q) ||
          p.pl_type?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [placesData, placeCategoryFilter, placeSearch]);

  const placeSummary = summaryData || {
    total: placesData.length,
    clean_count: placesData.filter((p) => p.pl_status === 'C').length,
    dirty_count: placesData.filter((p) => p.pl_status === 'D').length,
    in_progress_count: placesData.filter((p) => p.pl_status === 'I').length,
    maintenance_count: placesData.filter((p) => p.pl_status === 'M').length,
    overdue_count: placesData.filter((p) => Boolean(p.is_overdue)).length,
  };

  // --- Products & Stock computed data ---
  const productsWithStock = useMemo(() => {
    if (productSource === 'HK') {
      const prods = Array.isArray(hkProductsData) ? hkProductsData : [];
      const stockList = Array.isArray(hkStocksData) ? hkStocksData : [];
      const stockMap = new Map<number, any>();
      stockList.forEach((s) => {
        if (s.sl_id) stockMap.set(Number(s.sl_id), s);
      });

      return prods.map((p) => {
        const s = stockMap.get(Number(p.pr_id));
        return {
          id: p.pr_id,
          name: p.pr_name,
          code: p.pr_code || '',
          group: p.gp_name || p.group_name || p.pr_group || 'General',
          uom: p.pr_uom || 'PCS',
          rate: p.pr_rate || p.pr_srate || 0,
          image: p.pr_image || s?.sl_image || '',
          stock: s ? Number(s.sl_closing || 0) : 0,
          opening: s ? Number(s.sl_opening || 0) : 0,
          purchase: s ? Number(s.sl_purchase || 0) : 0,
          issue: s ? Number(s.sl_issue || 0) : 0,
          wastage: s ? Number(s.sl_wastage || 0) : 0,
        };
      });
    } else {
      const prods = Array.isArray(storeProductsData) ? storeProductsData : [];
      const stockList = Array.isArray(storeStocksData) ? storeStocksData : [];
      const stockMap = new Map<number, any>();
      stockList.forEach((s) => {
        if (s.sl_id) stockMap.set(Number(s.sl_id), s);
      });

      return prods.map((p: any) => {
        const s = stockMap.get(Number(p.pr_id));
        return {
          id: p.pr_id,
          name: p.pr_name,
          code: p.pr_code || '',
          group: p.gp_name || p.group_name || p.pr_grname || p.pr_group || 'Store Item',
          uom: p.pr_uom || p.pr_suom || p.pr_puom || 'PCS',
          rate: Number(p.pr_srate || p.pr_prate || p.pr_rate || 0),
          image: p.pr_image || s?.sl_image || '',
          stock: s ? Number(s.sl_closing || 0) : 0,
          opening: s ? Number(s.sl_opening || 0) : 0,
          purchase: s ? Number(s.sl_purchase || 0) : 0,
          issue: s ? Number(s.sl_issue || 0) : 0,
          wastage: s ? Number(s.sl_wastage || 0) : 0,
        };
      });
    }
  }, [
    productSource,
    hkProductsData,
    hkStocksData,
    storeProductsData,
    storeStocksData,
  ]);

  const availableGroups = useMemo(() => {
    const set = new Set<string>();
    productsWithStock.forEach((p) => {
      if (p.group) set.add(String(p.group));
    });
    return ['ALL', ...Array.from(set)];
  }, [productsWithStock]);

  const filteredProducts = useMemo(() => {
    let list = productsWithStock;
    if (selectedGroup !== 'ALL') {
      list = list.filter((p) => String(p.group) === selectedGroup);
    }
    if (productSearch.trim()) {
      const q = productSearch.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.code?.toLowerCase().includes(q) ||
          String(p.group).toLowerCase().includes(q)
      );
    }
    return list;
  }, [productsWithStock, selectedGroup, productSearch]);

  const stockStats = useMemo(() => {
    const total = productsWithStock.length;
    const inStock = productsWithStock.filter((p) => p.stock > 0).length;
    const lowStock = productsWithStock.filter((p) => p.stock > 0 && p.stock <= 10).length;
    const outOfStock = productsWithStock.filter((p) => p.stock <= 0).length;
    return { total, inStock, lowStock, outOfStock };
  }, [productsWithStock]);

  // Handle Clean place
  const handleOpenPlaceClean = (place: HKPublicPlace) => {
    setCleanDialog({ open: true, place });
    setSelectedEmployee(0);
    setTargetPlaceStatus('C');
    setSelectedPlaceTasks([]);
  };

  const handleSavePlaceClean = async () => {
    if (!selectedEmployee) {
      ShowToast('Please select an employee', 'error');
      return;
    }
    if (!cleanDialog.place) return;

    setIsSavingClean(true);
    try {
      await insertPublicCleaning({
        cl_company: cid,
        cl_date: moment(gdata.today).format('YYYY/MM/DD HH:mm:ss'),
        cl_place: cleanDialog.place.pl_id,
        cl_statusfrom: cleanDialog.place.pl_status || 'D',
        cl_statusto: targetPlaceStatus,
        cl_employee: selectedEmployee,
        cl_narr: selectedPlaceTasks.join(', '),
        cl_flag: '1',
      }).unwrap();
      ShowToast('Cleaning logged successfully');
      setCleanDialog({ open: false, place: null });
      refetchPlaces();
      refetchSummary();
    } catch (err: any) {
      ShowToast(err?.data?.message || 'Failed to log cleaning', 'error');
    } finally {
      setIsSavingClean(false);
    }
  };

  const employees = employeeData?.map((e) => ({ id: e.em_id, label: e.em_name })) || [];

  return (
    <View style={styles.container}>
      {/* Top Header */}
      <View style={styles.header}>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}><T>Housekeeping Status</T></Text>
          <Text style={styles.headerSubtitle}>
            {activeTab === 'ROOMS' && `${roomCounts.dirty} Dirty of ${roomCounts.all} Rooms`}
            {activeTab === 'PUBLIC_AREAS' && `${placeSummary.dirty_count} Areas Need Cleaning`}
            {activeTab === 'PRODUCTS_STOCK' && `${stockStats.inStock} of ${stockStats.total} Products In Stock`}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.refreshIconBtn}
          onPress={onRefresh}
          disabled={refreshing}
        >
          <FontAwesome name="refresh" size={16} color="#146C94" />
        </TouchableOpacity>
      </View>

      {/* Main Tab Segmented Control */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'ROOMS' && styles.tabButtonActive]}
          onPress={() => setActiveTab('ROOMS')}
          activeOpacity={0.8}
        >
          <FontAwesome
            name="bed"
            size={14}
            color={activeTab === 'ROOMS' ? '#ffffff' : '#475569'}
            style={{ marginRight: 6 }}
          />
          <Text style={[styles.tabText, activeTab === 'ROOMS' && styles.tabTextActive]}>
            <T>Rooms (</T>{roomCounts.all})
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'PUBLIC_AREAS' && styles.tabButtonActive]}
          onPress={() => setActiveTab('PUBLIC_AREAS')}
          activeOpacity={0.8}
        >
          <FontAwesome
            name="building"
            size={14}
            color={activeTab === 'PUBLIC_AREAS' ? '#ffffff' : '#475569'}
            style={{ marginRight: 6 }}
          />
          <Text style={[styles.tabText, activeTab === 'PUBLIC_AREAS' && styles.tabTextActive]}>
            <T>Public Areas</T>
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tabButton, activeTab === 'PRODUCTS_STOCK' && styles.tabButtonActive]}
          onPress={() => setActiveTab('PRODUCTS_STOCK')}
          activeOpacity={0.8}
        >
          <FontAwesome
            name="cubes"
            size={14}
            color={activeTab === 'PRODUCTS_STOCK' ? '#ffffff' : '#475569'}
            style={{ marginRight: 6 }}
          />
          <Text style={[styles.tabText, activeTab === 'PRODUCTS_STOCK' && styles.tabTextActive]}>
            <T>Stock (</T>{productsWithStock.length})
          </Text>
        </TouchableOpacity>
      </View>

      {/* ======================================================== */}
      {/* TAB 1: ROOMS                                             */}
      {/* ======================================================== */}
      {activeTab === 'ROOMS' && (
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          keyboardShouldPersistTaps="handled"
        >
          {/* Room KPI Summary Chips */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.kpiScroll}>
            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'ALL' && styles.kpiChipActive,
                { borderColor: '#146C94' },
              ]}
              onPress={() => setRoomStatusFilter('ALL')}
            >
              <Text style={[styles.kpiValue, { color: '#146C94' }]}>{roomCounts.all}</Text>
              <Text style={styles.kpiLabel}><T>All</T></Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'D' && styles.kpiChipActive,
                { borderColor: '#dc2626' },
              ]}
              onPress={() => setRoomStatusFilter('D')}
            >
              <Text style={[styles.kpiValue, { color: '#dc2626' }]}>{roomCounts.dirty}</Text>
              <Text style={styles.kpiLabel}><T>Dirty</T></Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'V' && styles.kpiChipActive,
                { borderColor: '#16a34a' },
              ]}
              onPress={() => setRoomStatusFilter('V')}
            >
              <Text style={[styles.kpiValue, { color: '#16a34a' }]}>{roomCounts.vacant}</Text>
              <Text style={styles.kpiLabel}><T>Vacant</T></Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'O' && styles.kpiChipActive,
                { borderColor: '#2563eb' },
              ]}
              onPress={() => setRoomStatusFilter('O')}
            >
              <Text style={[styles.kpiValue, { color: '#2563eb' }]}>{roomCounts.occupied}</Text>
              <Text style={styles.kpiLabel}><T>Occupied</T></Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'B' && styles.kpiChipActive,
                { borderColor: '#d97706' },
              ]}
              onPress={() => setRoomStatusFilter('B')}
            >
              <Text style={[styles.kpiValue, { color: '#d97706' }]}>{roomCounts.block}</Text>
              <Text style={styles.kpiLabel}><T>Block</T></Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.kpiChip,
                roomStatusFilter === 'M' && styles.kpiChipActive,
                { borderColor: '#9333ea' },
              ]}
              onPress={() => setRoomStatusFilter('M')}
            >
              <Text style={[styles.kpiValue, { color: '#9333ea' }]}>{roomCounts.maintenance}</Text>
              <Text style={styles.kpiLabel}><T>Maint.</T></Text>
            </TouchableOpacity>
          </ScrollView>

          {/* Search Bar */}
          <View style={styles.searchBar}>
            <FontAwesome name="search" size={14} color="#94a3b8" style={{ marginRight: 8 }} />
            <RNTextInput
              style={styles.searchInput}
              placeholder={tp("Search room number or type...")}
              placeholderTextColor="#94a3b8"
              value={roomSearch}
              onChangeText={setRoomSearch}
            />
            {roomSearch.length > 0 && (
              <TouchableOpacity onPress={() => setRoomSearch('')}>
                <FontAwesome name="times-circle" size={16} color="#94a3b8" />
              </TouchableOpacity>
            )}
          </View>

          {isRoomsLoading ? (
            <ActivityIndicator size="large" color="#146C94" style={{ marginTop: 40 }} />
          ) : filteredRooms.length === 0 ? (
            <View style={styles.emptyContainer}>
              <FontAwesome name="bed" size={40} color="#cbd5e1" />
              <Text style={styles.emptyText}><T>No rooms matching the filter</T></Text>
            </View>
          ) : (
            <View style={styles.roomsGrid}>
              {filteredRooms.map((room) => {
                const roomColor = getRoomColor(room, gdata);
                const isDirty = room.rm_status === 'D';
                const isOccupied = room.rm_status === 'O';

                return (
                  <Card key={room.rm_id} style={[styles.roomCard, { borderLeftColor: roomColor }]}>
                    <Card.Content style={{ paddingVertical: 10, paddingHorizontal: 12 }}>
                      <View style={styles.roomHeaderRow}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.roomNumber}>{room.rm_name}</Text>
                          <Text style={styles.roomType}>
                            {room.rt_name} {room.rm_floor ? `• Floor ${room.rm_floor}` : ''}
                          </Text>
                        </View>
                        <View
                          style={[
                            styles.statusTag,
                            { backgroundColor: roomColor + '20' },
                          ]}
                        >
                          <Text style={[styles.statusTagText, { color: roomColor }]}>
                            {getRoomStatusLabel(room)}
                          </Text>
                        </View>
                      </View>

                      {/* Guest Info if Occupied */}
                      {isOccupied && room.ci_guest && (
                        <View style={styles.guestRow}>
                          <FontAwesome name="user" size={12} color="#64748b" style={{ marginRight: 6 }} />
                          <Text style={styles.guestName} numberOfLines={1}>
                            {room.ci_name || `Guest #${room.ci_guest}`}
                          </Text>
                        </View>
                      )}

                      {/* Badges */}
                      {renderRoomBadges(room)}

                      <Divider style={{ marginVertical: 8 }} />

                      {/* Housekeeping Action Button */}
                      <TouchableOpacity
                        style={[
                          styles.hkActionButton,
                          isDirty ? styles.hkActionButtonUrgent : styles.hkActionButtonDefault,
                        ]}
                        onPress={() => {
                          navigation.navigate('HouseKeeping', {
                            room: room.rm_id,
                            mode: 'ROOM',
                          });
                        }}
                        activeOpacity={0.8}
                      >
                        <FontAwesome
                          name="paint-brush"
                          size={14}
                          color={isDirty ? '#ffffff' : '#146C94'}
                          style={{ marginRight: 6 }}
                        />
                        <Text
                          style={[
                            styles.hkActionText,
                            isDirty ? styles.hkActionTextUrgent : styles.hkActionTextDefault,
                          ]}
                        >
                          {isDirty ? 'Clean Room (Dirty)' : 'Housekeeping / Log'}
                        </Text>
                      </TouchableOpacity>
                    </Card.Content>
                  </Card>
                );
              })}
            </View>
          )}
        </ScrollView>
      )}

      {/* ======================================================== */}
      {/* TAB 2: PUBLIC AREAS                                      */}
      {/* ======================================================== */}
      {activeTab === 'PUBLIC_AREAS' && (
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          keyboardShouldPersistTaps="handled"
        >
          {/* Summary KPI row */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.kpiScroll}>
            <View style={[styles.kpiChip, { borderColor: '#146C94' }]}>
              <Text style={[styles.kpiValue, { color: '#146C94' }]}>{placeSummary.total}</Text>
              <Text style={styles.kpiLabel}><T>Total Areas</T></Text>
            </View>
            <View style={[styles.kpiChip, { borderColor: '#16a34a' }]}>
              <Text style={[styles.kpiValue, { color: '#16a34a' }]}>{placeSummary.clean_count}</Text>
              <Text style={styles.kpiLabel}><T>Clean</T></Text>
            </View>
            <View style={[styles.kpiChip, { borderColor: '#dc2626' }]}>
              <Text style={[styles.kpiValue, { color: '#dc2626' }]}>{placeSummary.dirty_count}</Text>
              <Text style={styles.kpiLabel}><T>Needs Clean</T></Text>
            </View>
            <View style={[styles.kpiChip, { borderColor: '#0288d1' }]}>
              <Text style={[styles.kpiValue, { color: '#0288d1' }]}>{placeSummary.in_progress_count}</Text>
              <Text style={styles.kpiLabel}><T>In Progress</T></Text>
            </View>
            <View style={[styles.kpiChip, { borderColor: '#7b1fa2' }]}>
              <Text style={[styles.kpiValue, { color: '#7b1fa2' }]}>{placeSummary.maintenance_count}</Text>
              <Text style={styles.kpiLabel}><T>Maint.</T></Text>
            </View>
            {Boolean(placeSummary.overdue_count) && (
              <View style={[styles.kpiChip, { borderColor: '#b91c1c', backgroundColor: '#fef2f2' }]}>
                <Text style={[styles.kpiValue, { color: '#b91c1c' }]}>{placeSummary.overdue_count}</Text>
                <Text style={styles.kpiLabel}><T>Overdue</T></Text>
              </View>
            )}
          </ScrollView>

          {/* Category Filter Chips */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
            {publicCategories.map((cat) => (
              <TouchableOpacity
                key={cat}
                style={[
                  styles.categoryChip,
                  placeCategoryFilter === cat && styles.categoryChipActive,
                ]}
                onPress={() => setPlaceCategoryFilter(cat)}
              >
                <Text
                  style={[
                    styles.categoryChipText,
                    placeCategoryFilter === cat && styles.categoryChipTextActive,
                  ]}
                >
                  {cat === 'ALL' ? 'All Areas' : cat}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Search Bar */}
          <View style={styles.searchBar}>
            <FontAwesome name="search" size={14} color="#94a3b8" style={{ marginRight: 8 }} />
            <RNTextInput
              style={styles.searchInput}
              placeholder={tp("Search area...")}
              placeholderTextColor="#94a3b8"
              value={placeSearch}
              onChangeText={setPlaceSearch}
            />
            {placeSearch.length > 0 && (
              <TouchableOpacity onPress={() => setPlaceSearch('')}>
                <FontAwesome name="times-circle" size={16} color="#94a3b8" />
              </TouchableOpacity>
            )}
          </View>

          {isPlacesLoading ? (
            <ActivityIndicator size="large" color="#146C94" style={{ marginTop: 40 }} />
          ) : filteredPlaces.length === 0 ? (
            <View style={styles.emptyContainer}>
              <FontAwesome name="building" size={40} color="#cbd5e1" />
              <Text style={styles.emptyText}><T>No public places found</T></Text>
            </View>
          ) : (
            <View style={styles.placesList}>
              {filteredPlaces.map((place) => {
                const status = getPlaceStatusDetails(place.pl_status, place.is_overdue);
                const icon = getPlaceCategoryIcon(place.pl_type);

                return (
                  <Card key={place.pl_id} style={styles.placeCard}>
                    <Card.Content style={{ paddingVertical: 10, paddingHorizontal: 12 }}>
                      <View style={styles.placeHeaderRow}>
                        <View style={styles.placeIconBox}>
                          <FontAwesome name={icon} size={20} color={status.color} />
                        </View>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.placeName}>{place.pl_name}</Text>
                          <Text style={styles.placeSub}>
                            {place.pl_floor || place.pl_type} {place.pl_code ? `(${place.pl_code})` : ''}
                          </Text>
                        </View>
                        <View style={[styles.statusTag, { backgroundColor: status.bgColor }]}>
                          <Text style={[styles.statusTagText, { color: status.color }]}>
                            {status.label}
                          </Text>
                        </View>
                      </View>

                      {/* Clean info */}
                      <View style={styles.placeInfoRow}>
                        <FontAwesome name="clock-o" size={12} color="#64748b" style={{ marginRight: 6 }} />
                        <Text style={styles.placeInfoText}>
                          <T>Last Cleaned:</T> {place.pl_last_cleaned_at ? moment(place.pl_last_cleaned_at).fromNow() : 'Never'}
                          {place.cleaner_name ? ` by ${place.cleaner_name}` : ''}
                        </Text>
                      </View>

                      {place.pl_cleaning_freq_hrs ? (
                        <Text style={styles.freqText}>
                          <T>Target: Every</T> {place.pl_cleaning_freq_hrs} <T>hrs</T>
                        </Text>
                      ) : null}

                      <Divider style={{ marginVertical: 8 }} />

                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity
                          style={[
                            styles.hkActionButton,
                            place.is_overdue ? styles.hkActionButtonUrgent : styles.hkActionButtonDefault,
                            { flex: 1 },
                          ]}
                          onPress={() => handleOpenPlaceClean(place)}
                          activeOpacity={0.8}
                        >
                          <FontAwesome
                            name="check-circle"
                            size={14}
                            color={place.is_overdue ? '#ffffff' : '#146C94'}
                            style={{ marginRight: 6 }}
                          />
                          <Text
                            style={[
                              styles.hkActionText,
                              place.is_overdue ? styles.hkActionTextUrgent : styles.hkActionTextDefault,
                            ]}
                          >
                            <T>Quick Clean</T>
                          </Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                          style={[styles.hkActionButton, styles.hkActionButtonDefault, { width: 44, paddingHorizontal: 0 }]}
                          onPress={() => {
                            navigation.navigate('HouseKeeping', {
                              placeId: place.pl_id,
                              mode: 'PUBLIC_PLACE',
                            });
                          }}
                          activeOpacity={0.8}
                        >
                          <FontAwesome name="pencil" size={14} color="#146C94" />
                        </TouchableOpacity>
                      </View>
                    </Card.Content>
                  </Card>
                );
              })}
            </View>
          )}
        </ScrollView>
      )}

      {/* ======================================================== */}
      {/* TAB 3: PRODUCTS & STOCK                                  */}
      {/* ======================================================== */}
      {activeTab === 'PRODUCTS_STOCK' && (
        <ScrollView
          style={styles.scrollArea}
          contentContainerStyle={styles.scrollContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          keyboardShouldPersistTaps="handled"
        >
          {/* Sub-segmented Source Selector: HK Products vs Store Products */}
          <View style={styles.subToggleContainer}>
            <TouchableOpacity
              style={[
                styles.subToggleButton,
                productSource === 'HK' && styles.subToggleButtonActive,
              ]}
              onPress={() => {
                setProductSource('HK');
                setSelectedGroup('ALL');
              }}
              activeOpacity={0.8}
            >
              <FontAwesome
                name="tags"
                size={13}
                color={productSource === 'HK' ? '#ffffff' : '#475569'}
                style={{ marginRight: 6 }}
              />
              <Text
                style={[
                  styles.subToggleText,
                  productSource === 'HK' && styles.subToggleTextActive,
                ]}
              >
                <T>HK Products</T>
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.subToggleButton,
                productSource === 'STORE' && styles.subToggleButtonActive,
              ]}
              onPress={() => {
                setProductSource('STORE');
                setSelectedGroup('ALL');
              }}
              activeOpacity={0.8}
            >
              <FontAwesome
                name="archive"
                size={13}
                color={productSource === 'STORE' ? '#ffffff' : '#475569'}
                style={{ marginRight: 6 }}
              />
              <Text
                style={[
                  styles.subToggleText,
                  productSource === 'STORE' && styles.subToggleTextActive,
                ]}
              >
                <T>Store Products</T>
              </Text>
            </TouchableOpacity>
          </View>

          {/* Stock KPI summary */}
          <View style={styles.stockSummaryRow}>
            <View style={styles.stockSummaryCard}>
              <Text style={styles.stockSummaryValue}>{stockStats.total}</Text>
              <Text style={styles.stockSummaryLabel}><T>Total Items</T></Text>
            </View>
            <View style={styles.stockSummaryCard}>
              <Text style={[styles.stockSummaryValue, { color: '#16a34a' }]}>
                {stockStats.inStock}
              </Text>
              <Text style={styles.stockSummaryLabel}><T>In Stock</T></Text>
            </View>
            <View style={styles.stockSummaryCard}>
              <Text style={[styles.stockSummaryValue, { color: '#ea580c' }]}>
                {stockStats.lowStock}
              </Text>
              <Text style={styles.stockSummaryLabel}><T>Low Stock</T></Text>
            </View>
            <View style={styles.stockSummaryCard}>
              <Text style={[styles.stockSummaryValue, { color: '#dc2626' }]}>
                {stockStats.outOfStock}
              </Text>
              <Text style={styles.stockSummaryLabel}><T>Zero / Out</T></Text>
            </View>
          </View>

          {/* Search Bar */}
          <View style={styles.searchBar}>
            <FontAwesome name="search" size={14} color="#94a3b8" style={{ marginRight: 8 }} />
            <RNTextInput
              style={styles.searchInput}
              placeholder={`Search ${productSource === 'HK' ? 'HK' : 'store'} products...`}
              placeholderTextColor="#94a3b8"
              value={productSearch}
              onChangeText={setProductSearch}
            />
            {productSearch.length > 0 && (
              <TouchableOpacity onPress={() => setProductSearch('')}>
                <FontAwesome name="times-circle" size={16} color="#94a3b8" />
              </TouchableOpacity>
            )}
          </View>

          {/* Category / Group filter chips */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryScroll}>
            {availableGroups.map((grp) => (
              <TouchableOpacity
                key={grp}
                style={[
                  styles.categoryChip,
                  selectedGroup === grp && styles.categoryChipActive,
                ]}
                onPress={() => setSelectedGroup(grp)}
              >
                <Text
                  style={[
                    styles.categoryChipText,
                    selectedGroup === grp && styles.categoryChipTextActive,
                  ]}
                >
                  {grp === 'ALL' ? 'All Groups' : grp}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {isHKProductsLoading || isStoreProductsLoading || isHKStocksLoading || isStoreStocksLoading ? (
            <ActivityIndicator size="large" color="#146C94" style={{ marginTop: 40 }} />
          ) : filteredProducts.length === 0 ? (
            <View style={styles.emptyContainer}>
              <FontAwesome name="cubes" size={40} color="#cbd5e1" />
              <Text style={styles.emptyText}><T>No products found matching filter</T></Text>
            </View>
          ) : (
            <View style={styles.productList}>
              {filteredProducts.map((item) => {
                const isZero = item.stock <= 0;
                const isLow = item.stock > 0 && item.stock <= 10;
                const stockColor = isZero ? '#dc2626' : isLow ? '#ea580c' : '#16a34a';
                const stockBg = isZero ? '#fee2e2' : isLow ? '#ffedd5' : '#dcfce7';

                return (
                  <Card key={item.id} style={styles.productCard}>
                    <Card.Content style={{ paddingVertical: 10, paddingHorizontal: 12 }}>
                      <View style={styles.productHeaderRow}>
                        <View style={{ flex: 1, paddingRight: 8 }}>
                          <Text style={styles.productName}>{item.name}</Text>
                          <View style={styles.productMetaRow}>
                            <View style={styles.groupBadge}>
                              <Text style={styles.groupBadgeText}>{item.group}</Text>
                            </View>
                            {item.code ? (
                              <Text style={styles.productCode}><T>Code:</T> {item.code}</Text>
                            ) : null}
                            <Text style={styles.productUom}><T>UOM:</T> {item.uom}</Text>
                          </View>
                        </View>

                        {/* Stock Badge */}
                        <View style={[styles.stockBadge, { backgroundColor: stockBg }]}>
                          <Text style={[styles.stockQtyText, { color: stockColor }]}>
                            {item.stock}
                          </Text>
                          <Text style={[styles.stockUomText, { color: stockColor }]}>
                            {item.uom}
                          </Text>
                        </View>
                      </View>

                      {/* Stock Movement Breakdown */}
                      <View style={styles.stockDetailsRow}>
                        <View style={styles.stockDetailItem}>
                          <Text style={styles.stockDetailLabel}><T>Opening</T></Text>
                          <Text style={styles.stockDetailVal}>{item.opening}</Text>
                        </View>
                        <View style={styles.stockDetailItem}>
                          <Text style={styles.stockDetailLabel}><T>Purchase</T></Text>
                          <Text style={[styles.stockDetailVal, { color: '#16a34a' }]}>
                            +{item.purchase}
                          </Text>
                        </View>
                        <View style={styles.stockDetailItem}>
                          <Text style={styles.stockDetailLabel}><T>Issued</T></Text>
                          <Text style={[styles.stockDetailVal, { color: '#2563eb' }]}>
                            -{item.issue}
                          </Text>
                        </View>
                        <View style={styles.stockDetailItem}>
                          <Text style={styles.stockDetailLabel}><T>Wastage</T></Text>
                          <Text style={[styles.stockDetailVal, { color: '#dc2626' }]}>
                            -{item.wastage}
                          </Text>
                        </View>
                        <View style={styles.stockDetailItem}>
                          <Text style={styles.stockDetailLabel}><T>Balance</T></Text>
                          <Text style={[styles.stockDetailVal, { fontWeight: '700', color: stockColor }]}>
                            {item.stock}
                          </Text>
                        </View>
                      </View>
                    </Card.Content>
                  </Card>
                );
              })}
            </View>
          )}
        </ScrollView>
      )}

      {/* Quick Clean Modal for Public Places */}
      <Modal visible={cleanDialog.open} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <FontAwesome name="check-circle" size={18} color="#146C94" />
              <Text style={styles.modalTitle}>
                <T>Log Cleaning:</T> {cleanDialog.place?.pl_name}
              </Text>
              <TouchableOpacity onPress={() => setCleanDialog({ open: false, place: null })}>
                <FontAwesome name="close" size={18} color="#64748b" />
              </TouchableOpacity>
            </View>

            <ScrollView style={{ padding: 14, maxHeight: 400 }}>
              <Text style={styles.inputLabel}><T>Housekeeping Employee *</T></Text>
              <View style={styles.pickerBox}>
                <Picker
                  selectedValue={selectedEmployee}
                  onValueChange={setSelectedEmployee}
                >
                  <Picker.Item label="Select Attendant..." value={0} />
                  {employees.map((e) => (
                    <Picker.Item key={String(e.id)} label={e.label} value={e.id} />
                  ))}
                </Picker>
              </View>

              <Text style={styles.inputLabel}><T>New Cleanliness Status</T></Text>
              <View style={styles.pickerBox}>
                <Picker
                  selectedValue={targetPlaceStatus}
                  onValueChange={setTargetPlaceStatus}
                >
                  <Picker.Item label="Clean (Completed)" value="C" />
                  <Picker.Item label="Cleaning In Progress" value="I" />
                  <Picker.Item label="Under Maintenance" value="M" />
                  <Picker.Item label="Dirty (Needs Attention)" value="D" />
                </Picker>
              </View>

              <Text style={[styles.inputLabel, { marginTop: 10 }]}><T>Tasks Completed:</T></Text>
              {[
                'Swept & Mopped Floor',
                'Sanitized High-Touch Surfaces',
                'Emptied Trash & Waste Bins',
                'Restocked Soap & Paper Towels',
                'Disinfected Toilets & Urinals',
                'Wiped Glass & Mirrors',
                'Dusted Furniture & Fixtures',
                'Vacuumed Carpets & Rugs',
              ].map((task) => (
                <TouchableOpacity
                  key={task}
                  style={styles.taskCheckRow}
                  onPress={() => {
                    setSelectedPlaceTasks((prev) =>
                      prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task]
                    );
                  }}
                >
                  <Checkbox
                    value={selectedPlaceTasks.includes(task)}
                    onValueChange={() => {
                      setSelectedPlaceTasks((prev) =>
                        prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task]
                      );
                    }}
                  />
                  <Text style={styles.taskCheckLabel}>{task}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={styles.modalActionRow}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={() => setCleanDialog({ open: false, place: null })}
                disabled={isSavingClean}
              >
                <Text style={styles.modalCancelText}><T>Cancel</T></Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalSaveBtn,
                  (!selectedEmployee || isSavingClean) && { opacity: 0.6 },
                ]}
                onPress={handleSavePlaceClean}
                disabled={!selectedEmployee || isSavingClean}
              >
                <Text style={styles.modalSaveText}>
                  {isSavingClean ? 'Saving...' : 'Save Cleaning'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f1f5f9',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 8,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0f172a',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  refreshIconBtn: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#f0f9ff',
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    gap: 8,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#f1f5f9',
  },
  tabButtonActive: {
    backgroundColor: '#146C94',
  },
  tabText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  tabTextActive: {
    color: '#ffffff',
  },
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  kpiScroll: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  kpiChip: {
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    marginRight: 8,
    borderWidth: 1.5,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    minWidth: 70,
  },
  kpiChipActive: {
    backgroundColor: '#f8fafc',
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  kpiValue: {
    fontSize: 16,
    fontWeight: '700',
  },
  kpiLabel: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 2,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
    marginBottom: 8,
    paddingHorizontal: 12,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#0f172a',
    paddingVertical: 4,
  },
  roomsGrid: {
    paddingHorizontal: 12,
    gap: 8,
  },
  roomCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderLeftWidth: 5,
    marginBottom: 8,
    elevation: 1,
  },
  roomHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  roomNumber: {
    fontSize: 17,
    fontWeight: '700',
    color: '#0f172a',
  },
  roomType: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 1,
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  statusTagText: {
    fontSize: 11,
    fontWeight: '700',
  },
  guestRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  guestName: {
    fontSize: 12,
    color: '#334155',
    fontWeight: '500',
    flex: 1,
  },
  hkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  hkActionButtonUrgent: {
    backgroundColor: '#dc2626',
  },
  hkActionButtonDefault: {
    backgroundColor: '#f0f9ff',
    borderWidth: 1,
    borderColor: '#146C94',
  },
  hkActionText: {
    fontSize: 12,
    fontWeight: '600',
  },
  hkActionTextUrgent: {
    color: '#ffffff',
  },
  hkActionTextDefault: {
    color: '#146C94',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 50,
  },
  emptyText: {
    fontSize: 13,
    color: '#94a3b8',
    marginTop: 10,
  },
  categoryScroll: {
    paddingHorizontal: 12,
    marginBottom: 10,
  },
  categoryChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 16,
    marginRight: 6,
    backgroundColor: '#ffffff',
  },
  categoryChipActive: {
    backgroundColor: '#146C94',
    borderColor: '#146C94',
  },
  categoryChipText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#475569',
  },
  categoryChipTextActive: {
    color: '#ffffff',
  },
  placesList: {
    paddingHorizontal: 12,
  },
  placeCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginBottom: 8,
    elevation: 1,
  },
  placeHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  placeIconBox: {
    width: 38,
    height: 38,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  placeName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0f172a',
  },
  placeSub: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  placeInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  placeInfoText: {
    fontSize: 11,
    color: '#475569',
  },
  freqText: {
    fontSize: 10,
    color: '#94a3b8',
    marginTop: 2,
  },
  subToggleContainer: {
    flexDirection: 'row',
    backgroundColor: '#e2e8f0',
    borderRadius: 8,
    padding: 3,
    marginHorizontal: 12,
    marginTop: 10,
    marginBottom: 10,
  },
  subToggleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    borderRadius: 6,
  },
  subToggleButtonActive: {
    backgroundColor: '#146C94',
  },
  subToggleText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#475569',
  },
  subToggleTextActive: {
    color: '#ffffff',
  },
  stockSummaryRow: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    gap: 6,
    marginBottom: 10,
  },
  stockSummaryCard: {
    flex: 1,
    backgroundColor: '#ffffff',
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  stockSummaryValue: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0f172a',
  },
  stockSummaryLabel: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 2,
  },
  productList: {
    paddingHorizontal: 12,
  },
  productCard: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginBottom: 8,
    elevation: 1,
  },
  productHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  productName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0f172a',
    lineHeight: 18,
  },
  productMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  groupBadge: {
    backgroundColor: '#f1f5f9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  groupBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#475569',
  },
  productCode: {
    fontSize: 10,
    color: '#94a3b8',
  },
  productUom: {
    fontSize: 10,
    color: '#64748b',
  },
  stockBadge: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    minWidth: 55,
  },
  stockQtyText: {
    fontSize: 16,
    fontWeight: '800',
    lineHeight: 18,
  },
  stockUomText: {
    fontSize: 9,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  stockDetailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
    marginTop: 8,
  },
  stockDetailItem: {
    alignItems: 'center',
  },
  stockDetailLabel: {
    fontSize: 9,
    color: '#94a3b8',
  },
  stockDetailVal: {
    fontSize: 11,
    fontWeight: '600',
    color: '#334155',
    marginTop: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    gap: 8,
  },
  modalTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: '#0f172a',
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 4,
  },
  pickerBox: {
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 6,
    marginBottom: 10,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  taskCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    gap: 10,
  },
  taskCheckLabel: {
    fontSize: 12,
    color: '#334155',
  },
  modalActionRow: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: 10,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#475569',
  },
  modalSaveBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#146C94',
    alignItems: 'center',
  },
  modalSaveText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default HKStatusScreen;
